import { useCallback, useRef } from 'react';

import { useAppDispatch, useAppSelector } from '@/Application/Hooks/Redux';
import type { RootState } from '@/Infrastructure/State';
import { getBuyNowAvailablePaymentMethods } from '@/modules/buy-now/infrastructure/BuyNowReduxRepository';
import type { IAvailablePaymentMethodsResponse } from '@/modules/perseo-billing/interfaces';

export const selectBuyNowPaymentMethodsData = (state: RootState) =>
  state?.buyNow?.paymentMethods?.data;

export const selectBuyNowPaymentMethodsLoading = (state: RootState) =>
  state?.buyNow?.paymentMethods?.isLoading;

export const selectBuyNowPaymentMethodsError = (state: RootState) =>
  state.buyNow.paymentMethods.error;

const useBuyNowPaymentMethods = () => {
  const dispatch = useAppDispatch();
  const data = useAppSelector(selectBuyNowPaymentMethodsData);
  const isLoading = useAppSelector(selectBuyNowPaymentMethodsLoading);
  const error = useAppSelector(selectBuyNowPaymentMethodsError);
  const hasFetchedRef = useRef(false);

  const getBuyNowPaymentMethodsData = useCallback(
    async (force = false) => {
      if (!force && hasFetchedRef.current) {
        return;
      }
      hasFetchedRef.current = true;
      const { payload } = await dispatch(getBuyNowAvailablePaymentMethods());
      return payload as IAvailablePaymentMethodsResponse | undefined;
    },
    [dispatch]
  );

  return {
    data,
    isLoading,
    error,
    isError: Boolean(error),
    refetch: () => getBuyNowPaymentMethodsData(true),
    wallets: data?.wallets || [],
    paymentMethods: data?.paymentMethods || [],
    clubParis: data?.clubParis || null,
    sessionKey: data?.sessionKey || null,
    cencosudCardBINs: data?.cencosudCardBINs || []
  };
};

export default useBuyNowPaymentMethods;
